// src/ClinicalDataCollector/components/output/TreatmentPlan.jsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Box,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HealingIcon from '@mui/icons-material/Healing';
import PsychologyIcon from '@mui/icons-material/Psychology';

import { useSelector } from 'react-redux';
import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

import {
  buildTreatmentPlanContextText,
} from '../../../../utils/clinicalNotesFormatter';
import {
  generateTreatmentPlanFromAI,
} from '../../../../utils/services/treatmentPlanAiService';

const TreatmentPlan = ({
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
}) => {
  // 🔹 AdvancedMD ID desde Redux
  const { advancedMdPatientId } = useSelector(
    (state) => state.patientMapSelect
  );

  // 🔹 Contexto AdvancedMD
  const {
    medications,
    medicationsLoading,
    medicationsError,
    loadMedications,
    labs,
    labsLoading,
    labsError,
    loadLabs,
    allergies,
    allergiesLoading,
    allergiesError,
    loadAllergies,
  } = useContext(AdvancedMdContext);

  // 🔹 Estado IA
  const [aiAcute, setAiAcute] = useState([]);
  const [aiNonHcc, setAiNonHcc] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // 🔹 Meds / Labs / Allergies desde AdvancedMD
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadMedications(advancedMdPatientId);
    loadLabs(advancedMdPatientId);
    loadAllergies(advancedMdPatientId);
  }, [advancedMdPatientId, loadMedications, loadLabs, loadAllergies]);

  const anyBaseLoading =
    medicationsLoading || labsLoading || allergiesLoading;

  const hasBaseErrors =
    medicationsError || labsError || allergiesError;

  const hasAiData = aiAcute.length > 0 || aiNonHcc.length > 0;

  // 🔹 Handler IA
  const handleGenerateAiPlan = async () => {
    try {
      setAiLoading(true);
      setAiError(null);

      const contextText = buildTreatmentPlanContextText({
        annualWellnessNotes,
        followUpExamNotes,
        telehealthNotes,
        hospitalDischargeNotes,
        medications: Array.isArray(medications) ? medications : [],
        labs: Array.isArray(labs) ? labs : [],
        allergies: Array.isArray(allergies) ? allergies : [],
      });

      const result = await generateTreatmentPlanFromAI({
        contextText,
      });

      setAiAcute(result.acuteDiagnoses || []);
      setAiNonHcc(result.nonHccDiagnoses || []);
    } catch (err) {
      console.error('[TreatmentPlan] AI error:', err);
      setAiError(err);
      setAiAcute([]);
      setAiNonHcc([]);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Treatment Plan (AI-based Draft)"
        subheader={
          hasAiData
            ? 'AI-generated draft based on clinical notes, medications, labs and allergies.'
            : 'Click "Generate AI Treatment Plan" to build a draft from this patient’s real clinical data.'
        }
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<LocalHospitalIcon fontSize="large" color="primary" />}
      />
      <Divider />
      <CardContent>
        {/* Botón IA */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mb: 2,
            gap: 2,
          }}
        >
          {anyBaseLoading && (
            <Typography variant="body2" color="text.secondary">
              Loading clinical data (medications, labs, allergies)…
            </Typography>
          )}

          {!advancedMdPatientId && (
            <Typography variant="body2" color="text.secondary">
              Link this patient to AdvancedMD before generating the treatment plan.
            </Typography>
          )}

          <Button
            variant="contained"
            color="secondary"
            onClick={handleGenerateAiPlan}
            disabled={aiLoading || !advancedMdPatientId}
            startIcon={
              aiLoading ? <CircularProgress size={18} /> : <PsychologyIcon />
            }
          >
            {aiLoading ? 'Generating AI treatment plan…' : 'Generate AI Treatment Plan'}
          </Button>
        </Box>

        {hasBaseErrors && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some clinical data (medications, labs or allergies) could not be loaded.
            The AI treatment plan may be incomplete.
          </Alert>
        )}

        {aiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error generating AI treatment plan:{' '}
            {aiError.message || 'Unknown error'}
          </Alert>
        )}

        {!hasAiData && !aiLoading && (
          <Typography variant="body2" color="text.secondary">
            No AI treatment plan has been generated yet. Press the button above to create a draft plan.
          </Typography>
        )}

        {hasAiData && (
          <>
            {/* Acute / Temporary Diagnoses */}
            <Typography
              variant="h6"
              sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}
            >
              <HealingIcon sx={{ mr: 1 }} /> Acute / Temporary Diagnoses
            </Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ICD-10</TableCell>
                  <TableCell>Diagnosis</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Provider</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aiAcute.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{item.code || 'N/A'}</TableCell>
                    <TableCell>{item.diagnosis || 'N/A'}</TableCell>
                    <TableCell>{item.plan || 'N/A'}</TableCell>
                    <TableCell>{item.date || 'N/A'}</TableCell>
                    <TableCell>{item.provider || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Non-HCC Diagnoses */}
            <Typography
              variant="h6"
              sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}
            >
              <HealingIcon sx={{ mr: 1, transform: 'rotate(180deg)' }} /> Non-HCC
              Diagnoses
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Condition</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Provider</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aiNonHcc.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      {item.condition || item.diagnosis || 'N/A'}
                    </TableCell>
                    <TableCell>{item.plan || 'N/A'}</TableCell>
                    <TableCell>{item.date || 'N/A'}</TableCell>
                    <TableCell>{item.provider || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TreatmentPlan;
